import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaAddressBook,
  FaBullhorn,
  FaCheck,
  FaCheckDouble,
  FaClock,
  FaCog,
  FaExclamationTriangle,
  FaWhatsapp,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import { Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";
import { formatPhoneDisplay } from "../../utils/phone";
import "../dm/dm.css";
import "./work.css";

export const fmtTime = (iso) =>
  iso ? new Date(`${iso}${/Z|[+-]\d\d:\d\d$/.test(iso) ? "" : "Z"}`).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";

const STATUS_ICONS = { sent: FaCheck, delivered: FaCheckDouble, read: FaCheckDouble, failed: FaExclamationTriangle, queued: FaClock };

/** Staff pick a client (clients come from their DM projects); portal users only see their own company. */
export function useClients(api) {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    if (!api.isStaff) return;
    api
      .projects({ limit: 100 })
      .then((rows) => {
        const map = new Map();
        (rows || []).forEach((p) => p.tenant_id && map.set(p.tenant_id, p.Tenant_Name || `Client #${p.tenant_id}`));
        setClients(Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => null);
  }, [api]);
  return clients;
}

/** Approved template picker with one box per {{n}} parameter. */
function TemplateFields({ templates, value, onChange, nameHint = false }) {
  const tpl = templates.find((t) => t.name === value.template);
  return (
    <>
      <label className="wk-field">
        Approved template
        <select
          value={value.template}
          onChange={(e) => {
            const next = templates.find((t) => t.name === e.target.value);
            onChange({ template: e.target.value, language: next?.language || "en", params: Array(next?.params || 0).fill("") });
          }}
          required
        >
          <option value="">Pick a template</option>
          {templates.map((t) => (
            <option key={`${t.name}-${t.language}`} value={t.name}>
              {t.name} ({t.language}, {String(t.category || "").toLowerCase()})
            </option>
          ))}
        </select>
        {!templates.length ? (
          <span className="wk-field__hint">No approved templates yet. Create them in WhatsApp Manager; Meta approves them within a day.</span>
        ) : null}
      </label>
      {tpl ? <div className="wk-pack-caption" style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>{tpl.body}</div> : null}
      {(value.params || []).map((p, i) => (
        <label className="wk-field" key={i}>
          {`Value for {{${i + 1}}}`}
          <input
            value={p}
            onChange={(e) => onChange({ ...value, params: value.params.map((x, j) => (j === i ? e.target.value : x)) })}
            required
          />
          {nameHint && i === 0 ? <span className="wk-field__hint">Type {"{{name}}"} to use each person's first name.</span> : null}
        </label>
      ))}
    </>
  );
}

function useTemplates(api, tenantId, enabled = true) {
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled || (api.isStaff && !tenantId)) return;
    api
      .waTemplates(api.isStaff ? { tenant_id: tenantId } : undefined)
      .then((res) => {
        setTemplates(res?.templates || []);
        setError("");
      })
      .catch((err) => setError(errorMessage(err, "Could not load templates")));
  }, [api, tenantId, enabled]);
  return [templates, error];
}

function Thread({ api, contactId, onChanged }) {
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState({ template: "", language: "en", params: [] });
  const [busy, setBusy] = useState(false);
  const windowOpen = data?.contact?.Window_Open;
  const [templates, tplError] = useTemplates(api, data?.contact?.tenant_id, Boolean(data) && !windowOpen);

  const load = useCallback(async () => {
    try {
      setData(await api.waConversation(contactId));
    } catch (err) {
      notify.error(errorMessage(err, "Could not load the chat"));
    }
  }, [api, contactId, notify]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.waReply(contactId, windowOpen ? { text: text.trim() } : { template: tpl.template, language: tpl.language, params: tpl.params });
      setText("");
      setTpl({ template: "", language: "en", params: [] });
      await load();
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err, "Could not send"));
    } finally {
      setBusy(false);
    }
  };

  const toggleOptIn = async () => {
    try {
      await api.updateWaContact(contactId, { Opted_In: !data.contact.Opted_In, Opt_In_Source: "chat" });
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  if (!data) return <div className="wk-muted">Loading…</div>;
  const c = data.contact;
  return (
    <div className="wk-wa-thread">
      <div className="wk-wa-thread__head">
        <Avatar name={c.Name || c.Wa_Id} size={34} />
        <div style={{ flex: 1 }}>
          <strong>{c.Name || formatPhoneDisplay("", c.Wa_Id)}</strong>
          <div className="wk-muted" style={{ fontSize: 12 }}>
            {formatPhoneDisplay("", c.Wa_Id)}
            {c.Website_Lead_Id ? (
              <>
                {" · "}
                <Link to={api.link("website-leads")}>Lead #{c.Website_Lead_Id}</Link>
              </>
            ) : null}
          </div>
        </div>
        <label className="wk-check" style={{ fontSize: 13 }}>
          <input type="checkbox" checked={c.Opted_In} onChange={toggleOptIn} /> Agreed to offers
        </label>
      </div>
      <ol className="wk-wa-msgs" aria-label="Messages">
        {data.messages.map((m) => {
          const Icon = STATUS_ICONS[m.Status];
          return (
            <li key={m.Message_Id} className={`wk-wa-msg wk-wa-msg--${m.Direction}`}>
              {m.Referral?.headline ? <div className="wk-wa-msg__ref">From ad: {m.Referral.headline}</div> : null}
              <div style={{ whiteSpace: "pre-wrap" }}>{m.Body || `[${m.Msg_Type}]`}</div>
              <div className="wk-wa-msg__meta">
                {fmtTime(m.Created_On)}
                {m.Direction === "out" && Icon ? (
                  <span title={m.Error || m.Status} className={m.Status === "read" ? "is-read" : ""}>
                    {" "}
                    <Icon aria-label={m.Status} />
                  </span>
                ) : null}
              </div>
              {m.Error ? <div className="wk-wa-msg__err">{m.Error}</div> : null}
            </li>
          );
        })}
      </ol>
      <form onSubmit={send} className="wk-wa-compose">
        {windowOpen ? (
          <>
            <label className="wk-sr-only" htmlFor={`wa-reply-${contactId}`}>
              Reply
            </label>
            <textarea
              id={`wa-reply-${contactId}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a reply…"
              maxLength={4096}
              rows={2}
            />
            <button type="submit" className="dmp-btn" disabled={busy || !text.trim()}>
              Send
            </button>
          </>
        ) : (
          <div style={{ flex: 1 }}>
            <p className="wk-muted" style={{ marginTop: 0 }}>
              More than 24 hours since they last wrote, so WhatsApp only allows an approved template.
            </p>
            {tplError ? <div className="dmp-error">{tplError}</div> : null}
            <TemplateFields templates={templates} value={tpl} onChange={setTpl} />
            <button type="submit" className="dmp-btn" disabled={busy || !tpl.template}>
              Send template
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function InboxTab({ api, tenantId, contactId, setContactId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.waConversations({ tenant_id: api.isStaff ? tenantId || undefined : undefined, search: search || undefined, unread: unread ? 1 : undefined }));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load chats"));
    }
  }, [api, tenantId, search, unread]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="wk-wa">
      <div className="wk-wa-list">
        <div className="wk-cal-toolbar">
          <label className="wk-sr-only" htmlFor="wa-search">
            Search chats
          </label>
          <input id="wa-search" className="wk-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or number" style={{ flex: 1 }} />
          <button type="button" className="wk-chip" aria-pressed={unread} onClick={() => setUnread((v) => !v)}>
            Unread{data?.unread ? ` (${data.unread})` : ""}
          </button>
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}
        {!data ? (
          <div className="wk-muted">Loading…</div>
        ) : data.conversations.length ? (
          <ul className="wk-wa-convos">
            {data.conversations.map((c) => (
              <li key={c.Contact_Id}>
                <button
                  type="button"
                  className={`wk-wa-convo${c.Contact_Id === contactId ? " is-active" : ""}`}
                  onClick={() => setContactId(c.Contact_Id)}
                  aria-current={c.Contact_Id === contactId ? "true" : undefined}
                >
                  <Avatar name={c.Name || c.Wa_Id} size={30} />
                  <span className="wk-wa-convo__body">
                    <span className="wk-wa-convo__name">
                      {c.Name || formatPhoneDisplay("", c.Wa_Id)}
                      <span className="wk-muted">{c.Last_Message_At ? fmtShortDate(c.Last_Message_At) : ""}</span>
                    </span>
                    <span className="wk-wa-convo__last">
                      {c.Last_Direction === "out" ? "You: " : ""}
                      {c.Last_Message}
                    </span>
                  </span>
                  {c.Unread ? <span className="wk-wa-badge" aria-label={`${c.Unread} unread`}>{c.Unread}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty icon={FaWhatsapp} title="No chats yet">
            When customers message the client's WhatsApp number (or tap a Click-to-WhatsApp ad), chats appear here and new
            numbers become leads.
          </Empty>
        )}
      </div>
      <div className="wk-wa-pane">
        {contactId ? (
          <Thread key={contactId} api={api} contactId={contactId} onChanged={load} />
        ) : (
          <Empty icon={FaWhatsapp} title="Pick a chat to read and reply" />
        )}
      </div>
    </div>
  );
}

function BroadcastsTab({ api, tenantId }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ Name: "", tags: "", when: "" });
  const [tpl, setTpl] = useState({ template: "", language: "en", params: [] });
  const [templates, tplError] = useTemplates(api, tenantId, creating);
  const params = useMemo(() => (api.isStaff ? { tenant_id: tenantId } : undefined), [api, tenantId]);

  const load = useCallback(async () => {
    try {
      setRows((await api.waBroadcasts(params))?.broadcasts || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load broadcasts"));
    }
  }, [api, params]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createWaBroadcast({
        tenant_id: api.isStaff ? tenantId : undefined,
        Name: form.Name,
        Template_Name: tpl.template,
        Language: tpl.language,
        Params: tpl.params,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        Scheduled_At: form.when ? new Date(form.when).toISOString().slice(0, 16) : undefined,
      });
      notify.success(`Broadcast to ${res.Total} people ${form.when ? "scheduled" : "is going out now"}`);
      setCreating(false);
      setForm({ Name: "", tags: "", when: "" });
      setTpl({ template: "", language: "en", params: [] });
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not create the broadcast"));
    }
  };

  const cancel = async (b) => {
    if (!(await confirm({ title: "Stop broadcast", message: `Stop "${b.Name}"? People not reached yet will not get it.` }))) return;
    try {
      await api.cancelWaBroadcast(b.Broadcast_Id);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div>
      <div className="wk-cal-toolbar">
        <p className="wk-muted" style={{ margin: 0, flex: 1 }}>
          Broadcasts go only to contacts who agreed to receive offers, using a template Meta has approved.
        </p>
        {!creating ? (
          <button type="button" className="dmp-btn" onClick={() => setCreating(true)}>
            <FaBullhorn aria-hidden="true" /> New broadcast
          </button>
        ) : null}
      </div>
      {creating ? (
        <form className="dmp-card" onSubmit={submit} style={{ maxWidth: 640 }}>
          <label className="wk-field">
            Name (for you)
            <input value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} placeholder="Diwali offer, 2BHK buyers" required />
          </label>
          {tplError ? <div className="dmp-error">{tplError}</div> : null}
          <TemplateFields templates={templates} value={tpl} onChange={setTpl} nameHint />
          <label className="wk-field">
            Send to contacts tagged (optional)
            <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="buyers, diwali" />
            <span className="wk-field__hint">Leave empty to send to everyone who agreed.</span>
          </label>
          <label className="wk-field">
            When (optional)
            <input type="datetime-local" value={form.when} onChange={(e) => setForm((f) => ({ ...f, when: e.target.value }))} />
            <span className="wk-field__hint">Leave empty to send now.</span>
          </label>
          <div className="wk-actions-bar">
            <button type="submit" className="dmp-btn" disabled={!tpl.template || !form.Name.trim()}>
              {form.when ? "Schedule" : "Send now"}
            </button>
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}
      {!rows ? (
        <div className="wk-muted">Loading…</div>
      ) : rows.length ? (
        <table className="wk-table">
          <thead>
            <tr>
              <th>Broadcast</th>
              <th>Status</th>
              <th>People</th>
              <th>Sent</th>
              <th>Delivered</th>
              <th>Read</th>
              <th>Failed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.Broadcast_Id}>
                <td>
                  <strong>{b.Name}</strong>
                  <div className="wk-muted" style={{ fontSize: 12 }}>
                    {b.Template_Name} · {fmtTime(b.Scheduled_At || b.Created_On)}
                    {b.Audience?.tags?.length ? ` · tags: ${b.Audience.tags.join(", ")}` : ""}
                  </div>
                </td>
                <td>{b.Status}</td>
                <td>{b.Total}</td>
                <td>{b.Sent}</td>
                <td>{b.Delivered}</td>
                <td>{b.Read}</td>
                <td>{b.Failed}</td>
                <td>
                  {["Scheduled", "Sending"].includes(b.Status) ? (
                    <button type="button" className="dmp-link" onClick={() => cancel(b)}>
                      Stop
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty icon={FaBullhorn} title="No broadcasts yet" />
      )}
    </div>
  );
}

const parseRows = (text) =>
  text
    .split(/\r?\n/)
    .map((line) => line.split(/[,\t;]/).map((x) => x.trim()))
    .filter(([a]) => a)
    .map(([a, b]) => (/\d{6,}/.test(a.replace(/\D/g, "")) ? { phone: a, name: b } : { phone: b, name: a }));

function ContactsTab({ api, tenantId }) {
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [optedOnly, setOptedOnly] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ text: "", tags: "", source: "", consent: false });

  const load = useCallback(async () => {
    try {
      setData(await api.waContacts({ tenant_id: api.isStaff ? tenantId : undefined, search: search || undefined, opted_in: optedOnly ? 1 : undefined }));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load contacts"));
    }
  }, [api, tenantId, search, optedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => parseRows(form.text), [form.text]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.importWaContacts({
        tenant_id: api.isStaff ? tenantId : undefined,
        rows,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        source: form.source || undefined,
        opt_in_confirmed: form.consent,
      });
      notify.success(`${res.added} added, ${res.updated} updated${res.skipped ? `, ${res.skipped} skipped` : ""}`);
      setImporting(false);
      setForm({ text: "", tags: "", source: "", consent: false });
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not import"));
    }
  };

  const toggle = async (c) => {
    try {
      await api.updateWaContact(c.Contact_Id, { Opted_In: !c.Opted_In, Opt_In_Source: "manual" });
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div>
      <div className="wk-cal-toolbar">
        <label className="wk-sr-only" htmlFor="wa-contact-search">
          Search contacts
        </label>
        <input id="wa-contact-search" className="wk-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or number" />
        <button type="button" className="wk-chip" aria-pressed={optedOnly} onClick={() => setOptedOnly((v) => !v)}>
          Agreed to offers{data ? ` (${data.opted_in})` : ""}
        </button>
        {!importing ? (
          <button type="button" className="dmp-btn" style={{ marginLeft: "auto" }} onClick={() => setImporting(true)}>
            <FaAddressBook aria-hidden="true" /> Add contacts
          </button>
        ) : null}
      </div>
      {importing ? (
        <form className="dmp-card" onSubmit={submit} style={{ maxWidth: 640 }}>
          <label className="wk-field">
            One person per line: phone, name
            <textarea
              rows={6}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              placeholder={"9876543210, Ravi Kumar\n+91 90000 00001, Asha"}
              required
            />
            <span className="wk-field__hint">
              {rows.length} row(s). 10-digit numbers are taken as India (+91). You can paste from Excel.
            </span>
          </label>
          <label className="wk-field">
            Tags (optional)
            <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="buyers, expo-2026" />
          </label>
          <label className="wk-field">
            Where did they agree? (optional)
            <input value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder="Website form, site visit, expo" />
          </label>
          <label className="wk-check">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
            These people agreed to get WhatsApp messages from this business.
          </label>
          <div className="wk-actions-bar">
            <button type="submit" className="dmp-btn" disabled={!form.consent || !rows.length}>
              Add {rows.length || ""} contact(s)
            </button>
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setImporting(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data ? (
        <div className="wk-muted">Loading…</div>
      ) : data.contacts.length ? (
        <table className="wk-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Number</th>
              <th>Tags</th>
              <th>Last message</th>
              <th>Agreed to offers</th>
            </tr>
          </thead>
          <tbody>
            {data.contacts.map((c) => (
              <tr key={c.Contact_Id}>
                <td>{c.Name || "—"}</td>
                <td>{formatPhoneDisplay("", c.Wa_Id)}</td>
                <td>{c.Tags.join(", ") || "—"}</td>
                <td>{c.Last_Message_At ? fmtShortDate(c.Last_Message_At) : "—"}</td>
                <td>
                  <label className="wk-check" style={{ margin: 0 }}>
                    <input type="checkbox" checked={c.Opted_In} onChange={() => toggle(c)} aria-label={`${c.Name || c.Wa_Id} agreed to offers`} />
                    {c.Opted_In ? "Yes" : c.Opted_Out_On ? "Said stop" : "No"}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty icon={FaAddressBook} title="No contacts yet">
          People who message the number are added automatically. Add customers who agreed to offers to send them broadcasts.
        </Empty>
      )}
    </div>
  );
}

/** DM > WhatsApp: the client's WhatsApp inbox (leads), broadcasts and contacts. */
function WhatsAppPage({ api }) {
  const [params, setParams] = useSearchParams();
  const clients = useClients(api);
  const [tenantId, setTenantId] = useState(params.get("tenant") || "");
  const contactId = Number(params.get("contact")) || null;
  const tabs = [
    { key: "inbox", label: "Inbox" },
    { key: "broadcasts", label: "Broadcasts" },
    { key: "contacts", label: "Contacts" },
  ];
  const tab = tabs.some((t) => t.key === params.get("tab")) ? params.get("tab") : "inbox";
  const setQuery = (next) => {
    const merged = { tab, ...(tenantId ? { tenant: tenantId } : {}), ...(contactId ? { contact: contactId } : {}), ...next };
    Object.keys(merged).forEach((k) => (merged[k] === null || merged[k] === "") && delete merged[k]);
    setParams(merged, { replace: true });
  };
  const needsClient = api.isStaff && !tenantId && tab !== "inbox";

  return (
    <div className="dmp wk">
      <PageHead title="WhatsApp">
        {api.isStaff ? (
          <select
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setQuery({ tenant: e.target.value, contact: null });
            }}
            aria-label="Client"
            style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", minHeight: 38 }}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : null}
        {api.isStaff ? (
          <Link className="dmp-btn dmp-btn--ghost" to={api.link("work/connections")}>
            <FaCog aria-hidden="true" /> Numbers &amp; setup
          </Link>
        ) : null}
      </PageHead>
      <div className="dmp-tabs dmp-tabs--pills" role="tablist" aria-label="WhatsApp" style={{ marginBottom: 12 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`dmp-tab${tab === t.key ? " is-active" : ""}`}
            onClick={() => setQuery({ tab: t.key })}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="dmp-card">
        {needsClient ? (
          <Empty icon={FaWhatsapp} title="Pick a client to see their broadcasts and contacts" />
        ) : tab === "inbox" ? (
          <InboxTab api={api} tenantId={tenantId} contactId={contactId} setContactId={(id) => setQuery({ contact: id })} />
        ) : tab === "broadcasts" ? (
          <BroadcastsTab api={api} tenantId={tenantId} />
        ) : (
          <ContactsTab api={api} tenantId={tenantId} />
        )}
      </div>
    </div>
  );
}

export default WhatsAppPage;
